export type TenantResolutionSource = 'managed_subdomain' | 'slug' | 'default';

export interface TenantRoutingContext {
  tenantSlug?: string;
  source: TenantResolutionSource;
  isSetupHost: boolean;
  compatibilityMode: boolean;
}

const RESERVED_SUBDOMAINS = new Set(['setup', 'onboard', 'www']);

const normalizeHostname = (hostname: string): string => hostname.trim().toLowerCase();

const extractSlugFromPath = (pathname: string): string | undefined => {
  const setupMatch = pathname.match(/^\/setup\/([a-z0-9-]{3,40})(?:\/|$)/i);
  if (setupMatch?.[1]) return setupMatch[1].toLowerCase();

  const namespaceMatch = pathname.match(/^\/t\/([a-z0-9-]{3,40})(?:\/|$)/i);
  if (namespaceMatch?.[1]) return namespaceMatch[1].toLowerCase();

  return undefined;
};

const extractSlugFromManagedDomain = (hostname: string, baseDomain: string): string | undefined => {
  const normalizedHost = normalizeHostname(hostname);
  const normalizedBase = normalizeHostname(baseDomain);

  if (!normalizedHost || !normalizedBase || !normalizedHost.endsWith(`.${normalizedBase}`)) {
    return undefined;
  }

  const prefix = normalizedHost.slice(0, -(`.${normalizedBase}`).length);
  const parts = prefix.split('.').filter(Boolean);
  const candidate = parts[0];

  if (!candidate || RESERVED_SUBDOMAINS.has(candidate)) return undefined;
  if (!/^[a-z0-9-]{3,40}$/.test(candidate)) return undefined;

  return candidate;
};

export const resolveTenantRoutingContext = (input: {
  hostname?: string;
  pathname?: string;
  baseDomain?: string;
}): TenantRoutingContext => {
  const hostname = normalizeHostname(input.hostname || '');
  const pathname = input.pathname || '/';
  const baseDomain = (input.baseDomain || 'aspis.software').toLowerCase();

  const isSetupHost = hostname.startsWith('setup.') || hostname.startsWith('onboard.');

  const managedSubdomainSlug = extractSlugFromManagedDomain(hostname, baseDomain);
  if (managedSubdomainSlug) {
    return {
      tenantSlug: managedSubdomainSlug,
      source: 'managed_subdomain',
      isSetupHost,
      compatibilityMode: false,
    };
  }

  const slugFromPath = extractSlugFromPath(pathname);
  if (slugFromPath) {
    return {
      tenantSlug: slugFromPath,
      source: 'slug',
      isSetupHost,
      compatibilityMode: true,
    };
  }

  return {
    source: 'default',
    isSetupHost,
    compatibilityMode: true,
  };
};
